// client/src/components/CampForm.jsx
// Create/edit form for a Camp (rendered as "תכנית" in UI).
// Used in two places:
//   - Inside the "+ תכנית חדשה" modal on CampsListPage
//   - Inside the camp settings tab on CampDetailPage (edit modal)
//
// Dirty-tracking + error scroll-into-view + Hebrew error messages match
// the StudentForm pattern.

import { useEffect, useMemo, useRef, useState } from 'react';
import { ApiError } from '../lib/api.js';
import { ExclamationTriangleIcon } from './Icons.jsx';
import { useCamp, useUploadCampLogo, useDeleteCampLogo } from '../hooks/useCamps.js';

// Max logo upload before the server (4 MB) would reject it — checked client-side
// for an instant, friendly error rather than a round-trip.
const MAX_LOGO_BYTES = 4 * 1024 * 1024;

export default function CampForm({
  initial,
  onSubmit,
  onCancel,
  submitLabel,
  onDirtyChange,
}) {
  const [name, setName] = useState(initial?.name || '');
  const [description, setDescription] = useState(initial?.description || '');
  const [startDate, setStartDate] = useState(toDateInputValue(initial?.startDate));
  const [endDate, setEndDate] = useState(toDateInputValue(initial?.endDate));
  const [currency, setCurrency] = useState(initial?.currency || 'SHEKEL');
  const [voucherLen, setVoucherLen] = useState(initial?.voucherCodeLength || 6);
  const [errors, setErrors] = useState({});
  const [submitting, setSubmitting] = useState(false);
  const [topError, setTopError] = useState(null);
  const topErrorRef = useRef(null);

  const initialFlat = useMemo(
    () =>
      JSON.stringify({
        name: initial?.name || '',
        description: initial?.description || '',
        startDate: toDateInputValue(initial?.startDate),
        endDate: toDateInputValue(initial?.endDate),
        currency: initial?.currency || 'SHEKEL',
        voucherLen: initial?.voucherCodeLength || 6,
      }),
    [initial]
  );
  const currentFlat = JSON.stringify({ name, description, startDate, endDate, currency, voucherLen });
  const isDirty = currentFlat !== initialFlat;

  useEffect(() => {
    onDirtyChange?.(isDirty);
    return () => onDirtyChange?.(false);
  }, [isDirty, onDirtyChange]);

  useEffect(() => {
    if (topError && topErrorRef.current) {
      topErrorRef.current.scrollIntoView({ behavior: 'smooth', block: 'center' });
    }
  }, [topError]);

  function handleCancel() {
    if (isDirty && !window.confirm('יש שינויים שלא נשמרו.\nלבטל?')) return;
    onCancel();
  }

  async function handleSubmit(e) {
    e.preventDefault();
    setErrors({});
    setTopError(null);
    setSubmitting(true);
    try {
      await onSubmit({
        name: name.trim(),
        description: description.trim() || null,
        startDate: startDate ? new Date(startDate).toISOString() : null,
        endDate: endDate ? new Date(endDate).toISOString() : null,
        currency,
        voucherCodeLength: Number(voucherLen),
      });
    } catch (err) {
      if (err instanceof ApiError) {
        if (err.status === 422 && Array.isArray(err.details)) {
          const fieldErrors = {};
          for (const issue of err.details) fieldErrors[issue.path] = issue.message;
          setErrors(fieldErrors);
          setTopError('יש לתקן את השדות המסומנים ולנסות שוב.');
        } else if (err.status === 422) {
          setTopError(
            err.message && err.message !== err.code
              ? `שגיאת ולידציה: ${err.message}`
              : 'אחד השדות לא תקין. בדוק את הערכים ונסה שוב.'
          );
        } else if (err.status === 401) {
          setTopError('הסשן פג. נסה לרענן את הדף ולהיכנס מחדש.');
        } else if (err.status === 403) {
          setTopError('אין לך הרשאה לפעולה זו.');
        } else if (err.status >= 500) {
          setTopError(`שגיאת שרת (${err.status}). נסה שוב בעוד רגע.`);
        } else {
          setTopError(`שגיאה: ${err.message || err.code || 'לא צפויה'}`);
        }
      } else {
        setTopError('שגיאה לא צפויה. בדוק שיש חיבור אינטרנט ונסה שוב.');
      }
    } finally {
      setSubmitting(false);
    }
  }

  return (
    <form onSubmit={handleSubmit} className="space-y-4">
      {topError && (
        <div
          ref={topErrorRef}
          role="alert"
          className="rounded-2xl bg-joy-50 border border-joy-200 text-joy-900 px-4 py-3 text-sm font-medium flex items-start gap-2 animate-slide-up"
        >
          <ExclamationTriangleIcon className="w-5 h-5 shrink-0 mt-0.5" />
          <span>{topError}</span>
        </div>
      )}
      <Field label="שם התכנית" error={errors.name} required>
        <input
          type="text"
          className="input"
          value={name}
          onChange={(e) => setName(e.target.value)}
          required
          maxLength={100}
          autoFocus
        />
      </Field>
      <Field label="תיאור" error={errors.description}>
        <textarea
          className="input"
          rows={3}
          value={description}
          onChange={(e) => setDescription(e.target.value)}
          maxLength={500}
        />
      </Field>
      <div className="grid grid-cols-2 gap-3">
        <Field label="תחילת התכנית" error={errors.startDate}>
          <input
            type="date"
            className="input"
            value={startDate}
            onChange={(e) => setStartDate(e.target.value)}
          />
        </Field>
        <Field label="סיום התכנית" error={errors.endDate}>
          <input
            type="date"
            className="input"
            value={endDate}
            onChange={(e) => setEndDate(e.target.value)}
          />
        </Field>
      </div>

      <fieldset>
        <legend className="label mb-2">סוג ניקוד</legend>
        <div className="grid grid-cols-3 gap-2">
          <CurrencyOption code="SHEKEL" symbol="₪" label="שקל" selected={currency === 'SHEKEL'} onChange={setCurrency} />
          <CurrencyOption code="DOLLAR" symbol="$" label="דולר" selected={currency === 'DOLLAR'} onChange={setCurrency} />
          <CurrencyOption code="STAR" symbol="⭐" label="כוכב" selected={currency === 'STAR'} onChange={setCurrency} />
        </div>
        {errors.currency && <span className="field-error">{errors.currency}</span>}
      </fieldset>

      <label className="block">
        <span className="label">אורך קוד שובר</span>
        <select className="input" value={voucherLen} onChange={(e) => setVoucherLen(e.target.value)}>
          {[6, 7, 8, 9].map((n) => (
            <option key={n} value={n}>{n} ספרות</option>
          ))}
        </select>
        <span className="hint">מספר הספרות בקודי השוברים שיודפסו. שוברים קיימים ימשיכו לעבוד.</span>
      </label>

      {/* Logo upload — only once the camp exists (needs an id to PUT to).
          Saved immediately on pick, independent of this form's Save button. */}
      {initial?.id && <LogoField campId={initial.id} />}

      <div className="flex justify-end gap-2 pt-2">
        <button type="button" className="btn-secondary" onClick={handleCancel}>
          ביטול
        </button>
        <button type="submit" className="btn-primary" disabled={submitting}>
          {submitting ? 'שומר...' : submitLabel || 'שמור'}
        </button>
      </div>
    </form>
  );
}

// Program-logo upload. Reads the live camp (cache-shared with the page) for
// hasLogo + logoUpdatedAt, so it reactively reflects the current state. The
// preview <img> hits the auth'd GET /logo endpoint with a ?v=<updatedAt> cache
// buster. Upload/remove persist immediately (not gated on the form's Save).
function LogoField({ campId }) {
  const camp = useCamp(campId);
  const upload = useUploadCampLogo(campId);
  const remove = useDeleteCampLogo(campId);
  const inputRef = useRef(null);
  const [error, setError] = useState(null);

  const hasLogo = !!camp.data?.hasLogo;
  const version = camp.data?.logoUpdatedAt
    ? new Date(camp.data.logoUpdatedAt).getTime()
    : 0;
  const logoSrc = hasLogo ? `/api/camps/${campId}/logo?v=${version}` : null;
  const busy = upload.isPending || remove.isPending;

  async function onPick(e) {
    setError(null);
    const file = e.target.files?.[0];
    e.target.value = ''; // allow re-selecting the same file later
    if (!file) return;
    if (!file.type.startsWith('image/')) {
      setError('יש לבחור קובץ תמונה (PNG, JPG או SVG).');
      return;
    }
    if (file.size > MAX_LOGO_BYTES) {
      setError('הקובץ גדול מדי (עד 4MB).');
      return;
    }
    try {
      await upload.mutateAsync(file);
    } catch (err) {
      setError(
        err?.code === 'logo_invalid'
          ? 'הקובץ אינו תמונה תקינה. נסו PNG / JPG / SVG.'
          : err?.message || 'העלאת הלוגו נכשלה. נסו שוב.'
      );
    }
  }

  async function onRemove() {
    setError(null);
    try {
      await remove.mutateAsync();
    } catch (err) {
      setError(err?.message || 'מחיקת הלוגו נכשלה. נסו שוב.');
    }
  }

  return (
    <fieldset>
      <legend className="label mb-2">לוגו התכנית</legend>
      <div className="flex items-center gap-4">
        {/* Fixed square preview tile — mirrors how the logo prints on vouchers
            (square, contained, transparent). */}
        <div className="w-20 h-20 shrink-0 rounded-2xl border border-ink-200 bg-ink-50 grid place-items-center overflow-hidden">
          {logoSrc ? (
            <img src={logoSrc} alt="לוגו התכנית" className="w-full h-full object-contain" />
          ) : (
            <PhotoIcon className="w-7 h-7 text-ink-300" />
          )}
        </div>
        <div className="flex-1 min-w-0">
          <div className="flex flex-wrap gap-2">
            <button
              type="button"
              className="btn-secondary btn-sm"
              disabled={busy}
              onClick={() => inputRef.current?.click()}
            >
              {upload.isPending ? 'מעלה...' : hasLogo ? 'החלף לוגו' : 'העלה לוגו'}
            </button>
            {hasLogo && (
              <button
                type="button"
                className="btn-sm text-joy-700 hover:bg-joy-50 rounded-lg px-3 py-1.5 font-semibold transition-colors"
                disabled={busy}
                onClick={onRemove}
              >
                {remove.isPending ? 'מסיר...' : 'הסר'}
              </button>
            )}
          </div>
          <p className="hint">
            יונרמל אוטומטית למידה ריבועית אחידה לכל התכניות, ויודפס על השוברים.
          </p>
          {error && <span className="field-error">{error}</span>}
        </div>
      </div>
      <input
        ref={inputRef}
        type="file"
        accept="image/png,image/jpeg,image/svg+xml,image/webp"
        className="sr-only"
        onChange={onPick}
      />
    </fieldset>
  );
}

function PhotoIcon({ className = '' }) {
  return (
    <svg className={className} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" aria-hidden="true">
      <rect x="3" y="3" width="18" height="18" rx="2.5" />
      <circle cx="8.5" cy="8.5" r="1.6" />
      <path strokeLinecap="round" strokeLinejoin="round" d="M21 15l-5-5L5 21" />
    </svg>
  );
}

function Field({ label, error, required, children }) {
  return (
    <label className="block">
      <span className="label">
        {label} {required && <span className="text-joy-600">*</span>}
      </span>
      {children}
      {error && <span className="field-error">{error}</span>}
    </label>
  );
}

function CurrencyOption({ code, symbol, label, selected, onChange }) {
  return (
    <label
      className={`block rounded-2xl border-2 p-3 cursor-pointer text-center transition-all ${
        selected
          ? 'border-brand-500 bg-brand-50 shadow-card'
          : 'border-ink-200 hover:border-brand-300 hover:bg-brand-50/40'
      }`}
    >
      <input
        type="radio"
        name="currency"
        value={code}
        checked={selected}
        onChange={(e) => onChange(e.target.value)}
        className="sr-only"
      />
      <div className="text-3xl leading-none mb-1 font-display">{symbol}</div>
      <div className="text-xs font-bold text-ink-700">{label}</div>
    </label>
  );
}

function toDateInputValue(d) {
  if (!d) return '';
  const date = d instanceof Date ? d : new Date(d);
  if (Number.isNaN(date.getTime())) return '';
  // YYYY-MM-DD in local tz (good enough for a date-only input).
  const y = date.getFullYear();
  const m = String(date.getMonth() + 1).padStart(2, '0');
  const day = String(date.getDate()).padStart(2, '0');
  return `${y}-${m}-${day}`;
}
